import React, { useState } from "react";
import { SensorResponseDto } from "../common/ObjectTypes";
import { useTranslation } from "react-i18next";
import Pagination from "../common/Pagination";
import tailwindClassnames from "../common/TailwindClassnames";
import { SortedDown, SortedUp, SortingArrows } from "../common/ProjectIcons";

type SensorsListProps = {
  sensors: SensorResponseDto[];
  totalCount: number;
  take: number;
  offset: number;
  loading: boolean;
  onPageClick: Function;
  requestSort: Function;
  canEditSensor: boolean;
  onSelectedSensor: Function;
};

const SensorsList: React.FC<SensorsListProps> = ({
  sensors,
  totalCount,
  take,
  offset,
  loading,
  onPageClick,
  requestSort,
  canEditSensor,
  onSelectedSensor,
}) => {
  const { t } = useTranslation();
  const [sortDirection, setSortDirection] = useState("asc");
  const [sortByColumn, setSortByColumn] = useState("sensor_name");

  const handleSort = (column: string) => {
    if (sortByColumn === column) {
      const newDirection = sortDirection === "asc" ? "desc" : "asc";
      setSortDirection(newDirection);
      requestSort(column, newDirection);
    } else {
      setSortByColumn(column);
      setSortDirection("asc");
      requestSort(column, "asc");
    }
  };

  const getArrowDirection = (column: string) => {
    return sortByColumn === column ? (
      sortDirection === "asc" ? (
        <SortedDown />
      ) : (
        <SortedUp />
      )
    ) : (
      <SortingArrows />
    );
  };

  /**
   * Header sort control: renders the column label with the appropriate sort
   * indicator and requests a re-sort on click. `column` is the API sort key.
   */
  const sortableHeader = (label: string, column: string) => (
    <button
      type="button"
      className="inline-flex items-center gap-1 uppercase tracking-wider"
      onClick={() => handleSort(column)}
    >
      {label}
      {getArrowDirection(column)}
    </button>
  );

  return (
    <div>
      <table className="w-full">
        <thead>
          <tr>
            <th className={`${tailwindClassnames.tableCardHeadCell} w-1/3`}>
              {sortableHeader(t("Name"), "sensor_name")}
            </th>
            <th className={tailwindClassnames.tableCardHeadCell}>
              {sortableHeader(t("Model"), "model")}
            </th>
            <th className={tailwindClassnames.tableCardHeadCell}>
              {sortableHeader(t("Serial Number"), "serial_number")}
            </th>
            <th className={tailwindClassnames.tableCardHeadCell}>
              {sortableHeader(t("Purchase Date"), "purchase_date")}
            </th>
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr>
              <td
                colSpan={4}
                className="py-3 px-4 text-left text-sm text-primary-off-black dark:text-primary-off-white transition duration-300"
              >
                {t("Loading")}...
              </td>
            </tr>
          ) : sensors.length > 0 ? (
            sensors.map((sensor) => {
              return (
                <tr
                  key={sensor.sensor_id}
                  className={tailwindClassnames.tableCardRow}
                  onClick={() => onSelectedSensor(sensor)}
                >
                  <td
                    className={tailwindClassnames.tableCardCellPrimary}
                    title={sensor.sensor_name ?? undefined}
                  >
                    {sensor.sensor_name}
                  </td>
                  <td
                    className={tailwindClassnames.tableCardCell}
                    title={sensor.model ?? undefined}
                  >
                    {sensor.model}
                  </td>
                  <td
                    className={tailwindClassnames.tableCardCell}
                    title={sensor.serial_number ?? undefined}
                  >
                    {sensor.serial_number}
                  </td>
                  <td
                    className={tailwindClassnames.tableCardCell}
                    title={sensor.purchase_date ?? undefined}
                  >
                    {sensor.purchase_date}
                  </td>
                </tr>
              );
            })
          ) : (
            <tr>
              <td
                colSpan={4}
                className="text-center py-3 text-primary-off-black dark:text-primary-off-white transition duration-300"
              >
                {t("No results found")}
              </td>
            </tr>
          )}
        </tbody>
      </table>
      <Pagination
        showText={true}
        handlePageClick={onPageClick}
        pageCount={Math.max(1, Math.ceil(totalCount / take))}
        resultStart={offset + 1}
        initialPage={Math.floor(offset / take)}
        resultEnd={offset + take < totalCount ? offset + take : totalCount}
        totalCount={totalCount}
        take={take}
        loading={loading}
        mainclassname={tailwindClassnames.tableCardFooter}
      />
    </div>
  );
};

export default SensorsList;
